import { useAutocomplete } from 'admin';
import type { BankDetailsInfo, FullTradeCompanyNested } from 'api';
import type { AutocompleteProps } from 'ui';

import { AutocompleteField } from '../AutocompleteField/AutocompleteField';

export type BankDetailsAutocompleteProps = {
  name: string;
  label?: string;
  required?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  defaultValue?: BankDetailsInfo['id'];
  tradeCompanies?: FullTradeCompanyNested['id'][];
  onSelectOption?: (value: BankDetailsInfo) => void;
} & Omit<AutocompleteProps<BankDetailsInfo>, 'options' | 'defaultValue'>;

export const BankDetailsAutocomplete = ({
  name,
  label = 'Select company',
  size,
  required,
  onSelectOption,
  disabled = false,
  defaultValue,
  tradeCompanies,
  ...rest
}: BankDetailsAutocompleteProps) => {
  const { autocompleteProps } = useAutocomplete<BankDetailsInfo>({
    dataProviderName: 'accounts',
    defaultValue,
    filters: [
      {
        field: 'trade_companies',
        operator: 'eq',
        value: tradeCompanies,
      },
    ],
    onSearch: (value) => [
      {
        field: 'search',
        operator: 'contains',
        value,
      },
    ],
    resource: '/v1/bank-details/',
  });

  return (
    <AutocompleteField<BankDetailsInfo>
      {...autocompleteProps}
      getOptionLabel={(item) => item.name ?? ''}
      label={label}
      name={name}
      isOptionEqualToValue={(option, value) => {
        return value === undefined || option?.id?.toString() === (value?.id ?? value)?.toString();
      }}
      placeholder="Type to search"
      valueField="id"
      size={size}
      required={required}
      data-testid="bank-details-autocomplete"
      onSelectOption={onSelectOption}
      disabled={disabled}
      {...rest}
    />
  );
};
